import { ref, useContext, computed, readonly } from "@nuxtjs/composition-api";
import { useEvent, useUiNotification } from "@wemade-vsf/composables";
import { useQuoteCartStore } from "../../stores/quoteCart";
import { Logger } from "@wemade-vsf/core";
import { loadQuoteCartCommand } from "./commands/loadQuoteCartCommand";
import { loadTotalQtyCommand } from "./commands/loadTotalQtyCommand";
import { addItemCommand } from "./commands/addItemCommand";
import { removeItemCommand } from "./commands/removeItemCommand";
import { updateItemQtyCommand } from "./commands/updateItemQtyCommand";
function useQuoteCart() {
  const loading = ref(false);
  const error = ref({
    load: null,
    clear: null,
    addItem: null,
    removeItem: null,
    updateItemQty: null,
    requestQuote: null
  });
  const { app } = useContext();
  const { send: sendNotification } = useUiNotification();
  const context = app.$vsf;
  const quoteCartStore = useQuoteCartStore();
  const quoteCart = computed(() => quoteCartStore.quoteCart);
  const apiState = context.$magento.config.state;
  const load = async ({ realCart = false } = {}) => {
    Logger.debug("useQuoteCart/load");
    try {
      loading.value = true;
      const loadedCart = await loadQuoteCartCommand.execute(context, { realCart });
      quoteCartStore.$patch((state) => {
        state.quoteCart = loadedCart;
      });
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useQuoteCart/load", err);
    } finally {
      loading.value = false;
    }
  };
  const clear = async () => {
    Logger.debug("useQuoteCart/clear");
    try {
      loading.value = true;
      apiState.setQuoteCartId(null);
      const loadedCart = await loadQuoteCartCommand.execute(context, {});
      quoteCartStore.$patch((state) => {
        state.quoteCart = loadedCart;
      });
      error.value.clear = null;
    } catch (err) {
      error.value.clear = err;
      Logger.error("useQuoteCart/clear", err);
    } finally {
      loading.value = false;
    }
  };
  const loadTotalQty = async () => {
    Logger.debug("useQuoteCart/loadTotalQty");
    try {
      loading.value = true;
      const totalQty = await loadTotalQtyCommand.execute(context);
      quoteCartStore.$patch((state) => {
        state.quoteCart.total_quantity = totalQty;
      });
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useQuoteCart/loadTotalQty", err);
    } finally {
      loading.value = false;
    }
  };
  const addItem = async ({
    product,
    customOptions,
    quantity,
    productConfiguration
  }) => {
    Logger.debug("useQuoteCart/addItem", { product, quantity });
    try {
      loading.value = true;
      if (!apiState.getQuoteCartId()) {
        await load({ realCart: true });
      }
      const updatedCart = await addItemCommand.execute(context, {
        currentCart: quoteCart.value,
        product,
        customOptions,
        quantity,
        productConfiguration
      });
      useEvent("quoteCart:addItem", {
        product,
        productConfiguration,
        quantity
      });
      sendNotification({
        id: Symbol("quote-add-item"),
        icon: "check",
        type: "success",
        persist: false,
        message: app.i18n.t("Product added to quote")
      });
      error.value.addItem = null;
      quoteCartStore.$patch((state) => {
        state.quoteCart = updatedCart;
      });
    } catch (err) {
      error.value.addItem = err;
      Logger.error("useQuoteCart/addItem", err);
    } finally {
      loading.value = false;
    }
  };
  const removeItem = async ({ product }) => {
    Logger.debug("useQuoteCart/removeItem", { product });
    try {
      loading.value = true;
      const updatedCart = await removeItemCommand.execute(context, {
        currentCart: quoteCart.value,
        product
      });
      useEvent("quoteCart:removeItem", { product });
      error.value.removeItem = null;
      quoteCartStore.$patch((state) => {
        state.quoteCart = updatedCart;
      });
    } catch (err) {
      error.value.removeItem = err;
      Logger.error("useQuoteCart/removeItem", err);
    } finally {
      loading.value = false;
    }
  };
  const updateItemQty = async ({ product, quantity }) => {
    Logger.debug("useQuoteCart/updateItemQty", { product, quantity });
    if (quantity && quantity > 0) {
      try {
        loading.value = true;
        const prevCartItem = quoteCart.value.items.find((item) => item.uid === product.uid);
        const updatedCart = await updateItemQtyCommand.execute(context, {
          currentCart: quoteCart.value,
          product,
          quantity
        });
        useEvent("quoteCart:updateItemQty", {
          product,
          quantity,
          previousQty: (prevCartItem == null ? void 0 : prevCartItem.quantity) || 0
        });
        error.value.updateItemQty = null;
        quoteCartStore.$patch((state) => {
          state.quoteCart = updatedCart;
        });
      } catch (err) {
        error.value.updateItemQty = err;
        Logger.error("useQuoteCart/updateItemQty", err);
      } finally {
        loading.value = false;
      }
    }
  };
  const requestCustomerQuote = async () => {
    var _a;
    Logger.debug("useQuoteCart/requestCustomerQuote");
    try {
      loading.value = true;
      const { data } = await app.$vsf.$magento.api.requestCustomerQuote();
      error.value.requestQuote = null;
      if (data == null ? void 0 : data.requestCustomerQuote) {
        quoteCartStore.$patch((state) => {
          state.quoteCart = null;
        });
        apiState.setQuoteCartId(null);
        await load({ realCart: true });
      }
      sendNotification({
        id: Symbol("quote-request-customer"),
        icon: "check",
        type: "success",
        persist: false,
        message: app.i18n.t("Quote request sent")
      });
      return (_a = data == null ? void 0 : data.requestCustomerQuote) != null ? _a : false;
    } catch (err) {
      error.value.requestQuote = err;
      Logger.error("useQuoteCart/requestCustomerQuote", err);
      return false;
    } finally {
      loading.value = false;
    }
  };
  const requestGuestQuote = async (contact, customFields = []) => {
    var _a;
    Logger.debug("useQuoteCart/requestGuestQuote", { contact, customFields });
    try {
      loading.value = true;
      const { data } = await app.$vsf.$magento.api.requestGuestQuote({
        cartId: quoteCart.value.id,
        contact,
        customFields
      });
      error.value.requestQuote = null;
      if (data == null ? void 0 : data.requestGuestQuote) {
        quoteCartStore.$patch((state) => {
          state.quoteCart = null;
        });
        apiState.setQuoteCartId(null);
        await load({ realCart: true });
      }
      sendNotification({
        id: Symbol("quote-request-guest"),
        icon: "check",
        type: "success",
        persist: false,
        message: app.i18n.t("Quote request sent")
      });
      return (_a = data == null ? void 0 : data.requestGuestQuote) != null ? _a : false;
    } catch (err) {
      error.value.requestQuote = err;
      Logger.error("useQuoteCart/requestGuestQuote", err);
      return false;
    } finally {
      loading.value = false;
    }
  };
  const mergeQuotes = async () => {
    var _a, _b;
    const currentQuoteId = apiState.getQuoteCartId();
    const quote = await app.$vsf.$magento.api.customerQuoteCart();
    const newQuoteId = (_b = (_a = quote == null ? void 0 : quote.data) == null ? void 0 : _a.customerQuoteCart) == null ? void 0 : _b.id;
    try {
      if (currentQuoteId && newQuoteId && currentQuoteId !== newQuoteId) {
        const { data } = await app.$vsf.$magento.api.mergeQuotes({
          sourceCartId: currentQuoteId,
          destinationCartId: newQuoteId
        });
        if (data == null ? void 0 : data.mergeCarts) {
          quoteCartStore.$patch((state) => {
            state.quoteCart = data.mergeCarts;
          });
        }
        apiState.setQuoteCartId(newQuoteId);
      } else {
        quoteCartStore.$patch((state) => {
          var _a2;
          state.quoteCart = (_a2 = quote == null ? void 0 : quote.data) == null ? void 0 : _a2.customerQuoteCart;
        });
      }
    } catch {
      quoteCartStore.$patch((state) => {
        var _a2;
        state.quoteCart = (_a2 = quote == null ? void 0 : quote.data) == null ? void 0 : _a2.customerQuoteCart;
      });
    }
  };
  const canAddToQuote = (product, qty = 1) => {
    var _a, _b;
    if ((product == null ? void 0 : product.__typename) === "ConfigurableProduct") {
      return !!((_b = (_a = product == null ? void 0 : product.configurable_product_options_selection) == null ? void 0 : _a.variant) == null ? void 0 : _b.uid);
    }
    const inStock = (product == null ? void 0 : product.stock_status) || "";
    const stockLeft = (product == null ? void 0 : product.only_x_left_in_stock) === null ? true : qty <= (product == null ? void 0 : product.only_x_left_in_stock);
    return inStock !== "OUT_OF_STOCK" && stockLeft;
  };
  return {
    loading: readonly(loading),
    error: readonly(error),
    quoteCart,
    load,
    clear,
    loadTotalQty,
    addItem,
    removeItem,
    updateItemQty,
    requestCustomerQuote,
    requestGuestQuote,
    mergeQuotes,
    canAddToQuote
  };
}
export {
  useQuoteCart
};
